import { Controller } from "@hotwired/stimulus"

// Clamps a content area to a maximum height and reveals a toggle button when
// the content overflows. Used e.g. for the AI summary, whose height grows as
// the streamed text arrives.
export default class extends Controller {
  static targets = ["toggle", "expandLabel", "collapseLabel"]
  static values = {
    maxHeight: { type: Number, default: 200 },
    expanded: { type: Boolean, default: false }
  }

  connect() {
    this.element.style.setProperty("--expandable-max-height", `${this.maxHeightValue}px`)
    this.element.classList.toggle("expandable-expanded", this.expandedValue)
    this.updateLabels()
    this.checkOverflow()

    // Content arrives/streams asynchronously, so re-check on every size change.
    this.observer = new ResizeObserver(() => this.checkOverflow())
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  toggle(event) {
    event?.preventDefault()
    this.expandedValue = !this.expandedValue
  }

  expandedValueChanged(_value, previous) {
    this.updateLabels()

    // No animation on the initial render.
    if (previous === undefined) return

    this.animate()
  }

  // CSS can't transition to `max-height: none`, so animate between explicit
  // pixel values and only drop the cap once the transition has finished.
  animate() {
    const el = this.element
    const expanded = this.expandedValue

    // Pin the current height as the transition's starting point.
    el.style.maxHeight = `${el.clientHeight}px`
    el.classList.toggle("expandable-expanded", expanded)
    void el.offsetHeight // force reflow

    el.style.maxHeight = expanded ? `${el.scrollHeight}px` : `${this.maxHeightValue}px`

    const onEnd = (event) => {
      if (event.target !== el || event.propertyName !== "max-height") return
      el.removeEventListener("transitionend", onEnd)
      // Let streamed content keep growing when open; restore the CSS clamp when closed.
      el.style.maxHeight = expanded ? "none" : ""
      this.checkOverflow()
    }
    el.addEventListener("transitionend", onEnd)
  }

  updateLabels() {
    if (this.hasExpandLabelTarget) this.expandLabelTarget.hidden = this.expandedValue
    if (this.hasCollapseLabelTarget) this.collapseLabelTarget.hidden = !this.expandedValue
  }

  checkOverflow() {
    if (!this.hasToggleTarget) return

    // Keep the toggle visible while expanded, otherwise only when overflowing.
    const overflowing = this.element.scrollHeight > this.maxHeightValue
    this.toggleTarget.hidden = !this.expandedValue && !overflowing
  }
}
